/* Member governance UI for the Manager org-settings plane (#342).
 *
 * Owns only the UI state (which form is open, what is being typed, which
 * member a modal is aimed at). Everything that reads or writes a member comes
 * in through `governance`; the per-member export buttons come in through
 * `exportSlotsFor` as opaque props, so this file needs no export imports.
 *
 * Stays MOUNTED on both views and gates internally: the modals below render
 * OUTSIDE the settings gate exactly as they did inline, so switching to the
 * exports view does not destroy an open dialog or a half-typed form. */
import { useState } from 'react';
import { Plus, Loader2, Copy, Check } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConfirmModal } from '../../components/atoms/Modal';
import { CardGrid } from '../../components/cards/cardKit';
import { useAuthStore } from '../../stores/authStore';
import { EditLimitsModal } from './EditLimitsModal';
import { MemberCard } from './MemberCard';
import type { MemberGovernance } from './useMemberGovernance';
import type { OrgMember } from './memberTypes';

/** Export-button props injected per member card. Opaque here — the handlers
 *  live with the export state in index.tsx until #343 moves them. */
export interface MemberExportSlots {
  onExport: () => void;
  exporting: boolean;
  exportError: string | null;
  onSettlementExport: () => void;
  settlementExporting: boolean;
  settlementExportError: string | null;
  onOrderAuditExport: () => void;
  orderAuditExporting: boolean;
  orderAuditExportError: string | null;
}

export interface MembersPanelProps {
  /** The panel stays mounted on both views and gates internally. */
  view: 'settings' | 'exports';
  adminMode?: boolean;
  selectedOrgId: string;
  orgQueryParam: string;
  selectedOrg: { id: string; name: string } | null;
  governance: MemberGovernance;
  exportSlotsFor: (member: OrgMember) => MemberExportSlots;
}

export function MembersPanel({
  view,
  adminMode,
  selectedOrgId,
  orgQueryParam,
  selectedOrg,
  governance: gov,
  exportSlotsFor,
}: MembersPanelProps) {
  const currentUserId = useAuthStore((s) => s.user?.id) ?? null;
  const currentUserRole = useAuthStore((s) => s.user?.role) ?? null;
  const navigate = useNavigate();
  const location = useLocation();
  const [showForm, setShowForm] = useState(false);
  const [username, setUsername] = useState('');
  const [showManagerForm, setShowManagerForm] = useState(false);
  const [managerUsername, setManagerUsername] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editUsername, setEditUsername] = useState('');
  const [resetTarget, setResetTarget] = useState<OrgMember | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OrgMember | null>(null);
  const [limitsTarget, setLimitsTarget] = useState<OrgMember | null>(null);

  const isTrader = (m: OrgMember) => m.role === 'trader';

  // Form/target guards and the form-close timing stay here with the UI state;
  // the requests themselves live in useMemberGovernance. `onCreated` fires at
  // exactly the point the old inline handlers cleared the form.
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    // #460 — was `adminMode && !selectedOrgId`, which caught only the EMPTY
    // selection. A stale localStorage id is non-empty, so it fell through and
    // POSTed an organization that no longer exists. The relay resolves that id
    // straight off the query string and inserts against it without checking the
    // organization exists (resolveOrgId + POST /traders, relay/src/routes/
    // manager.ts), so the request reached the database rather than being
    // refused. The control is disabled in both states now — this stays as the
    // guard of record for a submit that reaches here any other way.
    if (!gov.canCreateTrader) return;
    await gov.createTrader(username, () => { setUsername(''); setShowForm(false); });
  };

  const handleCreateManager = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!managerUsername.trim()) return;
    if (!gov.canCreateManager || !selectedOrg) return;
    await gov.createManager(managerUsername, () => { setManagerUsername(''); setShowManagerForm(false); });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await gov.deleteMember(deleteTarget);
    setDeleteTarget(null);
  };

  // Clears the previous password BEFORE the request and the target after, in
  // that order — the banner must never show a stale password mid-flight.
  const handleResetPassword = async () => {
    if (!resetTarget) return;
    gov.clearGeneratedPassword();
    await gov.resetPassword(resetTarget);
    setResetTarget(null);
  };

  const handleEditSave = async (id: string) => {
    gov.setError(null);
    if (await gov.renameMember(id, editUsername)) setEditingId(null);
  };

  return (
    <>
      {view === 'settings' && (
        <>
      {/* The heading stays "Traders" — #295 is explicit that this is NOT a rename
          to Members. Create Trader and Create Manager sit together as two named,
          separate actions; opening either closes the other so it is always
          unambiguous which kind of account is being created. */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Traders</h2>
        <div className="flex shrink-0 items-center gap-2">
          {/* #460 — disabled-with-a-reason when no organization resolves, the
              same treatment Create Manager has carried since #295. These two sit
              inches apart, so the previous disagreement (one refusing with an
              explanation, the other accepting and then silently doing nothing)
              read as a broken button rather than an unmet precondition.
              No title in the enabled state, deliberately: the sibling names its
              target org, but a manager has no `selectedOrg` to name and would
              be told "…in undefined". Adding nothing keeps today's behavior. */}
          <button onClick={() => { setShowForm(!showForm); setShowManagerForm(false); gov.clearGeneratedPassword(); }}
            disabled={!gov.canCreateTrader}
            title={
              gov.canCreateTrader
                ? undefined
                : 'Select an organization first — a trader is created in the organization chosen above'
            }
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50">
            <Plus size={14} /> Create Trader
          </button>
          {currentUserRole === 'admin' && (
            // Outlined rather than a second blue button: two identical primaries
            // read as one action duplicated. Disabled-with-a-reason (not hidden)
            // when no org resolves, so an admin who lands here with a stale or
            // empty selection is told why instead of finding nothing.
            <button
              onClick={() => { setShowManagerForm(!showManagerForm); setShowForm(false); gov.clearGeneratedPassword(); }}
              disabled={!gov.canCreateManager}
              title={
                gov.canCreateManager
                  ? `Create a manager in ${selectedOrg?.name}`
                  : 'Select an organization first — a manager is created in the organization chosen above'
              }
              className="border-border text-foreground hover:bg-muted/50 flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus size={14} /> Create Manager
            </button>
          )}
        </div>
      </div>

      {/* #460 — gated on the same predicate as the button, mirroring the manager
          form below. An organization can stop resolving while this form is open
          (the selection is cleared, or the org list reloads without it), and an
          open form whose Create button silently does nothing IS the defect this
          issue is fixing. `showForm` itself is untouched, so the form — and
          anything already typed into it — comes straight back when a valid
          organization is selected again. */}
      {showForm && gov.canCreateTrader && (
        <form onSubmit={handleCreate} className="bg-muted/50 border-border mb-4 space-y-3 rounded-lg border p-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-xs">Username</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)}
              className="bg-card border-border text-foreground w-full rounded-md border px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
              placeholder="trader1" />
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={gov.submitting}
              className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50">
              {gov.submitting && <Loader2 size={12} className="animate-spin" />} Create
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="text-muted-foreground hover:text-foreground rounded-md px-3 py-1.5 text-xs">Cancel</button>
          </div>
        </form>
      )}

      {showManagerForm && gov.canCreateManager && selectedOrg && (
        <form onSubmit={handleCreateManager} aria-label="Create manager" className="bg-muted/50 border-border mb-4 space-y-3 rounded-lg border p-4">
          {/* Naming the org is the whole point: the toolbar selection is
              authoritative but it lives far from this form, and it survives
              reloads via localStorage. Stating the target here means an admin
              cannot create a manager in an org they had forgotten was selected. */}
          <p className="text-muted-foreground text-xs">
            Creating a manager in <span className="text-foreground font-medium">{selectedOrg.name}</span>
          </p>
          <div>
            <label className="text-muted-foreground mb-1 block text-xs" htmlFor="create-manager-username">Username</label>
            <input id="create-manager-username" value={managerUsername} onChange={(e) => setManagerUsername(e.target.value)}
              className="bg-card border-border text-foreground w-full rounded-md border px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
              placeholder="manager1" />
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={gov.managerSubmitting || !managerUsername.trim()}
              className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50">
              {gov.managerSubmitting && <Loader2 size={12} className="animate-spin" />} Create manager
            </button>
            <button type="button" onClick={() => setShowManagerForm(false)}
              className="text-muted-foreground hover:text-foreground rounded-md px-3 py-1.5 text-xs">Cancel</button>
          </div>
        </form>
      )}

      <PasswordBanner password={gov.generatedPassword} onDismiss={gov.clearGeneratedPassword} />
      {gov.error && <div className="mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{gov.error}</div>}

      {/* #444 — the unselected-admin scope is checked FIRST, ahead of both
          branches below. It is not a loading state (nothing was requested), and
          it must not fall through to the grid: `members` still holds the
          previously selected organization's cards, so reaching the grid here
          would show organization A's members while none is selected. Falling
          through to "No members yet." would be just as wrong — it describes an
          organization the admin has not chosen. */}
      {gov.awaitingOrg ? (
        <p className="text-muted-foreground py-8 text-center text-sm">
          Select an organization to manage its members.
        </p>
      ) : gov.loading ? (
        <div className="flex justify-center py-8"><Loader2 className="text-muted-foreground h-5 w-5 animate-spin" /></div>
      ) : gov.members.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">No members yet.</p>
      ) : (
        <CardGrid variant="wide">
          {gov.members.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              usage={gov.usageByUserId[member.id]}
              editing={editingId === member.id}
              editUsername={editUsername}
              onStartEdit={() => { setEditingId(member.id); setEditUsername(member.username); }}
              onCancelEdit={() => setEditingId(null)}
              onSaveEdit={() => handleEditSave(member.id)}
              onEditUsernameChange={setEditUsername}
              onResetPassword={isTrader(member) || gov.canGovern(member) ? () => setResetTarget(member) : undefined}
              onDelete={isTrader(member) || gov.canGovern(member) ? () => setDeleteTarget(member) : undefined}
              canGovernMember={gov.canGovern(member)}
              onToggleUseOrgKeys={
                isTrader(member) || member.id === currentUserId
                  ? (next: boolean) => gov.toggleUseOrgKeys(member.id, next)
                  : undefined
              }
              onToggleBotsEnabled={
                isTrader(member)
                  ? (next: boolean) => gov.toggleBotsEnabled(member.id, next)
                  : undefined
              }
              orgBotsEnabled={gov.orgBotsEnabled}
              onToggleOscarEnabled={
                // Trader rows: any manager/admin can grant. Non-trader rows
                // (admins/managers, incl. self): ADMIN callers only — the
                // relay's rules-only widened PATCH path mirrors this gate.
                isTrader(member) || currentUserRole === 'admin'
                  ? (next: boolean) => gov.toggleOscarEnabled(member.id, next)
                  : undefined
              }
              onToggleOneClickEnabled={
                isTrader(member) || currentUserRole === 'admin'
                  ? (next: boolean) => gov.toggleOneClickEnabled(member.id, next)
                  : undefined
              }
              onToggleLt2Enabled={
                // TRADER ROWS ONLY — deliberately narrower than the two chips
                // above, which also offer admin callers a grant on manager and
                // admin rows. LT2 is inert without an explicit size ceiling,
                // and the only control for that ceiling is Edit limits, which
                // is itself `isTrader`-gated — so granting it on a row that can
                // never be given a ceiling would seat a permanently inactive
                // chip. The ticket's wording ("for trader accounts") points the
                // same way. Caller authority and org scope are untouched: this
                // is the same org-scoped trader PATCH every chip here uses.
                isTrader(member)
                  ? (next: boolean) => gov.toggleLt2Enabled(member.id, next)
                  : undefined
              }
              onEditLimits={isTrader(member) ? () => setLimitsTarget(member) : undefined}
              {...exportSlotsFor(member)}
              onViewDetail={() =>
                navigate(`${location.pathname}/trader/${member.id}`, {
                  state: {
                    username: member.username,
                    orgId: adminMode ? selectedOrgId : undefined,
                  },
                })
              }
            />
          ))}
        </CardGrid>
      )}
      </>
      )}

      <ConfirmModal
        isOpen={!!resetTarget}
        onClose={() => setResetTarget(null)}
        onConfirm={handleResetPassword}
        title="Reset Password"
        message={`Generate a new password for "${resetTarget?.username}"? The current password will be invalidated immediately.`}
        confirmLabel="Reset Password"
        variant="danger"
      />
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        // Role-aware since #295: this plane can now delete a manager, and a
        // dialog headed "Delete Trader" over a manager's name is a false
        // statement at the exact moment the reader is confirming a destructive
        // action.
        title={deleteTarget?.role === 'manager' ? 'Delete Manager' : 'Delete Trader'}
        message={`Are you sure you want to delete ${deleteTarget?.role === 'manager' ? 'manager' : 'trader'} "${deleteTarget?.username}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />

      <EditLimitsModal
        isOpen={!!limitsTarget}
        onClose={() => setLimitsTarget(null)}
        trader={limitsTarget}
        orgQueryParam={orgQueryParam}
        currentUsage={limitsTarget ? gov.usageByUserId[limitsTarget.id] : undefined}
        onSaved={gov.applyMemberPatch}
      />
    </>
  );
}

function PasswordBanner({
  password,
  onDismiss,
}: {
  password: string | null;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  if (!password) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mb-4 rounded-lg border border-green-700/50 bg-green-900/20 p-4 dark:border-green-800">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-green-600 dark:text-green-400">Generated Password</span>
        <button onClick={onDismiss} className="text-muted-foreground hover:text-foreground text-xs">Dismiss</button>
      </div>
      <div className="flex items-center gap-2">
        <code className="bg-card border-border flex-1 rounded border px-3 py-1.5 font-mono text-sm text-green-700 dark:text-green-300">
          {password}
        </code>
        <button onClick={handleCopy} title="Copy" className="text-muted-foreground hover:text-foreground hover:bg-muted rounded p-1.5">
          {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
        </button>
      </div>
      <p className="text-muted-foreground mt-2 text-xs">Save this password now. It will not be shown again.</p>
    </div>
  );
}
